/**
 * giftEffects — plays a category-specific sonic + haptic signature when a
 * new live gift lands. Keeps everything under 100 lines and free of
 * external assets: sounds are synthesized via the Web Audio API so we ship
 * a single JS module rather than 40 mp3s.
 *
 * Muting is user-controlled via `localStorage.getItem('pandax_gift_sound')`
 * so viewers can silence the room without touching device volume.
 *
 * All public entrypoints are safe to call on any device — every touch of
 * `window.AudioContext` / `navigator.vibrate` is guarded so SSR + iOS
 * pre-gesture states don't throw.
 */

import { isBroadcasting, playTone } from "@/lib/liveAudioAlerts";

const SOUND_STORAGE_KEY = "pandax_gift_sound";

export function isSoundEnabled() {
  // SECURITY NOTE: Sound-toggle preference only. No PII / auth data.
  try { return window.localStorage.getItem(SOUND_STORAGE_KEY) !== "0"; }
  catch { return true; }
}

export function setSoundEnabled(on) {
  // SECURITY NOTE: Sound-toggle preference only. No PII / auth data.
  try { window.localStorage.setItem(SOUND_STORAGE_KEY, on ? "1" : "0"); }
  catch { /* private-mode Safari — nothing we can do */ }
}

/** Play a single sine/triangle tone with an ADSR fade. */
function tone(freq, duration = 0.18, type = "sine", gain = 0.08, delay = 0) {
  playTone({ freq, duration, type, gain, delay });
}

/** Rising chord — used for premium DM gifts. */
function chord(freqs, duration = 0.25) {
  freqs.forEach((f, i) => tone(f, duration, "triangle", 0.05, i * 0.03));
}

// Preset signatures — each animation lasts <1.5s so the room stays responsive.
const SIGNATURES = {
  buzz: {
    // A rapid low-frequency square-wave burr; matches the "buzz" gift theme.
    sound: () => { tone(70, 0.6, "square", 0.06); tone(90, 0.6, "square", 0.06, 0.05); },
    vibrate: [40, 30, 40, 30, 60],
    anim: "pandax-gift-buzz",
    scale: 1.6,
  },
  live_menu: {
    // Playful two-note pop for action requests.
    sound: () => { tone(660, 0.12, "triangle", 0.08); tone(880, 0.15, "triangle", 0.07, 0.08); },
    vibrate: [30, 20, 30],
    anim: "pandax-gift-pop",
    scale: 2.0,
  },
  dms: {
    // Chime chord — "message inbound".
    sound: () => chord([523, 659, 784], 0.35),
    vibrate: [15, 40, 15],
    anim: "pandax-gift-shimmer",
    scale: 2.2,
  },
  classic: {
    // Warm arpeggio — matches the existing feel.
    sound: () => { tone(523, 0.12, "sine", 0.08); tone(784, 0.14, "sine", 0.07, 0.1); },
    vibrate: [20],
    anim: "pandax-gift-burst",
    scale: 2.4,
  },
};

/**
 * Fire the signature for a given gift category. Returns the CSS keyframe
 * name so the caller can plug it straight into `style.animation`.
 */
export function fireGiftEffect(category, { silent = false } = {}) {
  const sig = SIGNATURES[category] || SIGNATURES.classic;
  // Publishers never get audible alerts — see liveAudioAlerts.js.
  if (!silent && !isBroadcasting() && isSoundEnabled()) {
    try { sig.sound(); } catch { /* audio pipeline can fail on some browsers */ }
  }
  if (typeof navigator !== "undefined" && navigator.vibrate && sig.vibrate) {
    try { navigator.vibrate(sig.vibrate); } catch { /* silent — TypeError on some tvs */ }
  }
  return { anim: sig.anim, scale: sig.scale };
}
